import type { TFunction } from "i18next";
import type { ProjectedHumanStep, RequestAudience } from "../../../api/threads/liveMessages";
import { refusalText } from "../../../ui/feedback/refusalWords";
import type { ViewerRefusal } from "../page/viewerActions";

/**
 * Whom the waiting person step waits for (W-22 slice 1): module C names the request's audience, the web only words it.
 * For a viewer who can't answer, why not comes first where that is the reason (a closed Run, no permission to write).
 * `hostName` is `null` when the viewer hosts the Run.
 */
export function requestWaitText(step: ProjectedHumanStep, refusal: ViewerRefusal | null, hostName: string | null, t: TFunction): string {
  if (refusal === "closed" || refusal === "missing_permission") return refusalText(t, refusal);
  if (step.audience === "host") return hostName === null ? t("threads.waits.yours") : t("threads.waits.host", { name: hostName });
  return t(WAITS_FOR[step.audience]);
}

/**
 * The Input badge's words where the turn is another person's: on a Run someone else hosts, a request its writers answer
 * (a saved Run's, its host alone; a live Run's) waits on the host who runs it, "Waiting for Hanna" (Johannes, 4 Nov
 * 07:42; live Runs built provisionally). `null` otherwise, and the badge keeps Input: the viewer's own Run, or a review
 * its curators or the memory's owner decide, the viewer perhaps among them.
 */
export function requestTurnText(step: ProjectedHumanStep, hostName: string | null, t: TFunction): string | null {
  return HOSTS_TURN[step.audience] && hostName !== null ? t("threads.waits.hostTurn", { name: hostName }) : null;
}

/**
 * Whether the badge names the host for each audience module C sends: yes where the Run's writers answer, no where an
 * owner decides. Every audience is worded here, so a new one fails the typecheck until it is.
 */
const HOSTS_TURN: Readonly<Record<RequestAudience, boolean>> = {
  host: true,
  participants: true,
  change_set_curators: false,
  result_curators: false,
  memory_owner: false,
};

/** The open request after the Run left its Workflow: module C keeps it open (W22-D2, provisional), and no step shows it until the host resumes the Workflow. */
export function workflowLeftText(hostName: string | null, t: TFunction): string {
  return hostName === null ? t("threads.waits.workflowLeftHost") : t("threads.waits.workflowLeft", { name: hostName });
}

const WAITS_FOR: Readonly<Record<Exclude<RequestAudience, "host">, string>> = {
  participants: "threads.waits.participants",
  change_set_curators: "threads.waits.changeSetCurators",
  result_curators: "threads.waits.resultCurators",
  memory_owner: "threads.waits.memoryOwner",
};
